import React from 'react';
import LegalLayout from './LegalLayout';

export default function DataDeletion() {
  return (
    <LegalLayout
      title="FABTEXS AI | Data Deletion Policy"
      description="Learn how to request deletion of your account and personal data from FABTEXS AI."
      lastUpdated="18 February 2026"
    >
      <h2>1. Introduction</h2>
      <p>
        At FABTEXS AI, we respect your privacy and your right to control your
        personal information.
      </p>
      <p>
        This Data Deletion Policy explains how users may request deletion of
        their account, connected Meta (Facebook) data, WhatsApp Business
        integration data and other personal information processed by FABTEXS AI.
      </p>
      <p>
        This policy applies to all users of the FABTEXS AI platform, website,
        APIs, WhatsApp integrations, AI services, customer management tools and
        related services.
      </p>

      <h2>2. Requesting Data Deletion</h2>
      <p>
        If you wish to delete your account or request deletion of your personal
        information, you may contact us using the details below.
      </p>
      <p>
        <strong>Email:</strong>{' '}
        <a href="mailto:fabtexswholesale@gmail.com">fabtexswholesale@gmail.com</a>
      </p>
      <p>Please include:</p>
      <ul>
        <li>Registered email address</li>
        <li>Business name (if applicable)</li>
        <li>Registered WhatsApp Business number</li>
        <li>A clear request for deletion of your account and associated data</li>
      </ul>
      <p>
        For security purposes, we may request additional information to verify
        your identity before processing your request.
      </p>

      <h2>3. What Data May Be Deleted</h2>
      <p>
        Upon successful verification of your request, FABTEXS AI may permanently
        delete data associated with your account, including:
      </p>
      <ul>
        <li>User profile information</li>
        <li>Connected Meta account information</li>
        <li>Connected WhatsApp Business account information</li>
        <li>Customer records stored within FABTEXS AI</li>
        <li>Product catalog data</li>
        <li>AI conversation history</li>
        <li>Broadcast lists</li>
        <li>Uploaded files and documents</li>
        <li>Customer segmentation data</li>
        <li>Account preferences</li>
        <li>Platform activity associated with your account</li>
      </ul>

      <h2>4. Data That May Be Retained</h2>
      <p>Certain information may be retained where required for:</p>
      <ul>
        <li>Legal compliance</li>
        <li>Fraud prevention</li>
        <li>Security investigations</li>
        <li>Financial record keeping</li>
        <li>Tax obligations</li>
        <li>Dispute resolution</li>
        <li>Enforcement of our Terms of Service</li>
      </ul>
      <p>
        Such retained information will only be retained for the period required
        under applicable law.
      </p>

      <h2>5. Processing Time</h2>
      <p>
        Verified deletion requests are generally processed within{' '}
        <strong>30 days</strong>.
      </p>
      <p>
        If additional verification is required, processing may take longer. We
        will notify the user if additional time is needed.
      </p>

      <h2>6. Third-Party Services</h2>
      <p>
        FABTEXS AI integrates with third-party platforms including Meta&apos;s
        WhatsApp Business Platform.
      </p>
      <p>
        Deletion of information stored by third-party providers is governed by
        their respective privacy policies and terms.
      </p>
      <p>
        Users may also need to disconnect or remove permissions directly from
        their Meta or WhatsApp Business accounts where applicable.
      </p>

      <h2>7. Effect of Deletion</h2>
      <p>Once the deletion request has been completed:</p>
      <ul>
        <li>The FABTEXS AI account may be permanently removed.</li>
        <li>Access to the platform may be revoked.</li>
        <li>Deleted information generally cannot be recovered.</li>
        <li>
          Certain backup copies may remain for a limited period before permanent
          deletion as part of our disaster recovery procedures.
        </li>
      </ul>

      <h2>8. Contact Information</h2>
      <p>
        <strong>Company:</strong> FABTEXS<br />
        <strong>Brand:</strong> FABTEXS AI<br />
        <strong>Website:</strong>{' '}
        <a href="https://app.fabtexs.com">https://app.fabtexs.com</a>
        <br />
        <strong>Email:</strong>{' '}
        <a href="mailto:fabtexswholesale@gmail.com">fabtexswholesale@gmail.com</a>
      </p>

      <h2>9. Meta (Facebook) User Data</h2>
      <p>
        If you have connected your Meta (Facebook) account or WhatsApp Business
        account with FABTEXS AI, you may request deletion of your associated
        Platform Data by following the procedure described in this policy.
      </p>
      <p>
        FABTEXS AI will process verified requests in accordance with Meta
        Platform requirements and applicable laws.
      </p>
    </LegalLayout>
  );
}
